import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, map, of, switchMap, catchError } from 'rxjs';
import { environment } from '../../environments/environment';

export interface UserProfile {
  firstName?: string;
  lastName?: string;
  businessEmail?: string;
  countryLabel?: string;
  countryCode?: string;
  businessUnitCode?: string;
  businessUnitLabel?: string;
  contentLanguage?: string;
}

interface InternalUser {
  firstName?: string;
  lastName?: string;
  businessEmail?: string;
  mail?: string;
  /** Despite its name, this is the user's country name (e.g. "FRANCE"), not a BU code. */
  businessUnit?: string;
}

interface Country {
  countryCode?: string;
  countryLabel?: string;
}

interface BusinessUnit {
  businessUnitCode?: string;
  businessUnitLabel?: string;
  contentLanguage?: string;
}

interface CountryBusinessUnitCacheEntry {
  /** Cache key: the raw country name as returned by UM API's `businessUnit` field. */
  countryName: string;
  countryLabel: string;
  countryCode: string;
  businessUnitCode: string;
  businessUnitLabel: string;
  contentLanguage: string;
  cachedAt: number;
}

export const USER_PROFILE_COUNTRY_BU_CACHE_KEY = 'mass-mailing-portal:countryBusinessUnitCache';
const CACHE_KEY = USER_PROFILE_COUNTRY_BU_CACHE_KEY;
const CACHE_TTL_MS = 60 * 60 * 1000;

function toProfileFields(entry: CountryBusinessUnitCacheEntry): Partial<UserProfile> {
  return {
    countryLabel: entry.countryLabel,
    countryCode: entry.countryCode,
    businessUnitCode: entry.businessUnitCode,
    businessUnitLabel: entry.businessUnitLabel,
    contentLanguage: entry.contentLanguage,
  };
}

function isCacheEntry(value: unknown): value is CountryBusinessUnitCacheEntry {
  const entry = value as Partial<CountryBusinessUnitCacheEntry> | null;
  return (
    !!entry &&
    typeof entry.countryName === 'string' &&
    typeof entry.countryLabel === 'string' &&
    typeof entry.countryCode === 'string' &&
    typeof entry.businessUnitCode === 'string' &&
    typeof entry.businessUnitLabel === 'string' &&
    typeof entry.contentLanguage === 'string' &&
    typeof entry.cachedAt === 'number'
  );
}

@Injectable({ providedIn: 'root' })
export class UserProfileService {
  private readonly http = inject(HttpClient);
  private readonly oauthService = inject(OAuthService);

  /**
   * SPIKE (AZTGSA-10906): calls UM API v2 directly from the browser with the user's own
   * SSO access token, instead of going through the backend's GET /me. UM API's CORS
   * allow-list covers this app's origin(s) — confirmed for localhost:4300 via a preflight
   * probe; not yet confirmed for every deployed frontend origin.
   *
   * AZTGSA-12347: also resolves the user's country/default Business Unit by chaining the
   * UM API's user lookup with the /int/v1 countries and businessUnits reference-data
   * lookups, caching that part of the chain for 1h (see resolveCountryBusinessUnit).
   */
  getCurrentUserProfile(): Observable<UserProfile> {
    const gadId = this.getGadId();
    if (!gadId) {
      return of({});
    }

    const url = `${environment.umApiUrl}/internalUsers?gadId=${encodeURIComponent(gadId)}`;
    return this.http.get<InternalUser[]>(url).pipe(
      switchMap((users) => {
        const user = users?.[0];
        if (!user) {
          return of<UserProfile>({});
        }

        const nameProfile: UserProfile = {
          firstName: user.firstName,
          lastName: user.lastName,
          businessEmail: user.businessEmail ?? user.mail,
        };

        const countryName = user.businessUnit;
        if (!countryName) {
          return of(nameProfile);
        }

        return this.resolveCountryBusinessUnit(countryName).pipe(
          map((countryBusinessUnit) => ({ ...nameProfile, ...countryBusinessUnit })),
        );
      }),
    );
  }

  /**
   * Resolves the country/default Business Unit for a country name, reusing a
   * localStorage-cached result (keyed on that country name) when it is less than 1h old.
   * A country name that doesn't match the cached entry's key is always a cache miss,
   * even if the entry isn't expired yet — see AZTGSA-12347 design.md, Decision 4.
   */
  private resolveCountryBusinessUnit(countryName: string): Observable<Partial<UserProfile>> {
    const cached = this.readCache();
    if (cached?.countryName === countryName && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
      return of(toProfileFields(cached));
    }

    const countriesUrl = `${environment.referenceDataApiUrl}/countries?countryName=${encodeURIComponent(countryName)}`;
    return this.http.get<Country[]>(countriesUrl).pipe(
      switchMap((countries) => {
        const country = countries?.[0];
        if (!country?.countryCode || !country.countryLabel) {
          return of<Partial<UserProfile>>({});
        }

        const businessUnitsUrl = `${environment.referenceDataApiUrl}/businessUnits?countryCode=${encodeURIComponent(country.countryCode)}`;
        return this.http.get<BusinessUnit[]>(businessUnitsUrl).pipe(
          map((businessUnits) => businessUnits?.[0]),
          map((businessUnit) => {
            if (!businessUnit?.businessUnitCode) {
              return {};
            }

            const entry: CountryBusinessUnitCacheEntry = {
              countryName,
              countryLabel: country.countryLabel as string,
              countryCode: country.countryCode as string,
              businessUnitCode: businessUnit.businessUnitCode,
              businessUnitLabel: businessUnit.businessUnitLabel ?? '',
              contentLanguage: businessUnit.contentLanguage ?? '',
              cachedAt: Date.now(),
            };
            this.writeCache(entry);
            return toProfileFields(entry);
          }),
        );
      }),
      catchError(() => of<Partial<UserProfile>>({})),
    );
  }

  private readCache(): CountryBusinessUnitCacheEntry | undefined {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) {
        return undefined;
      }

      const parsed = JSON.parse(raw);
      return isCacheEntry(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  private writeCache(entry: CountryBusinessUnitCacheEntry): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
      // localStorage may be unavailable (private mode, quota exceeded, ...) — caching is best-effort.
    }
  }

  private getGadId(): string {
    const claims = this.oauthService.getIdentityClaims() as { sub?: string; external_id?: string } | null;
    const rawId = claims?.sub ?? claims?.external_id ?? '';
    return rawId.replace(/^GAD\\/, '');
  }
}
